import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import { isEqual } from "lodash";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import useSWR from "swr";

import { getDocumentByWorkspaceID, setDocumentArchieve } from "../../Api/Services/DocumentServices.js";
import { getPetNamesByWorkspaceId } from "../../Api/Services/PetServices.js";
import "./GlobalItemList.css";
import { ReactComponent as ViewIcon } from "../../Assets/Images/viewIcon.svg";
import useAuth from "../../ContextProvider/AuthProvider/AuthProvider.js";
import { useCategories } from "../../ContextProvider/CategoriesProvider/CategoriesProvider.js";
import { filterAscDescData, hexToRgba } from "../../Utils.js";
import DetailsModal from "../Common/DetailsModal.js";
import PawItemList from "../Common/PawItemList.js";
import { Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import styled from "styled-components";

export default function Document() {
  const { workspaceId: w_id } = useAuth();
  const { categories } = useCategories();
  const [pagination, setPagination] = useState({});
  const [pageCount, setPageCount] = useState(0);
  const [selDocumentData, setSelDocumentData] = useState([]);
  const fetchIdRef = useRef(0);
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [subcategoryOptions, setSubcategoryOptions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  let [selectedPetName, setSelectedPetName] = useState("");

  const [open, setOpen] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [archiveId, setArchiveId] = useState(null);
  
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const categoryId = searchParams.get('categoryId');
    const subcategoryId = searchParams.get('subcategoryId');
 
    
    if (categoryId) {
      setSelectedCategory(categoryId);
      
      const category = categories.find(cat => Number(cat.id) === Number(categoryId));
      
      if (category && category.sub_category) {
        const subOptions = category.sub_category.map(sub => ({
          name: sub.name,
          id: sub.id
        }));
        setSubcategoryOptions(subOptions);
        
        if (subcategoryId) {
          setSelectedSubcategory(subcategoryId);
        }
      }
    } else {
      setSelectedCategory(null);
    }
  }, [location.search, categories, selectedCategory]);


  const handleCategoryChange = (categoryName) => {

    if (!categoryName) {
      navigate('/document');
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      setSubcategoryOptions([]);

      return;
    }
    
    const category = categoryOptions.find(cat => cat.name === categoryName);
    const categoryId = category ? category.value : null;
    
    
    if (categoryId) {
      const newSearchParams = new URLSearchParams();
      newSearchParams.set('categoryId', categoryId);
      navigate(`/document?${newSearchParams.toString()}`);
      
      setSelectedSubcategory(null);
    } else {
      navigate('/document');
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      setSubcategoryOptions([]);
    }
  };

  const handleSubcategoryChange = (subcategoryName) => {

    if (!subcategoryName) {
      setSelectedSubcategory(null);
      return;
    }
    
    const subcategory = subcategoryOptions.map(sub => ({
      name: sub.name,
      value: sub.id
    })).find(sub => sub.name === subcategoryName);
    
    const subcategoryId = subcategory ? subcategory.value : null;
    
    if (subcategoryId && selectedCategory) {
      setSelectedCategory(subcategoryId);
      const newSearchParams = new URLSearchParams();
      newSearchParams.set('categoryId', selectedCategory);
      newSearchParams.set('subcategoryId', subcategoryId);
      navigate(`/document?${newSearchParams.toString()}`);
    } else {
    
      if (selectedCategory) {
        const newSearchParams = new URLSearchParams();
        newSearchParams.set('categoryId', selectedCategory);
        navigate(`/document?${newSearchParams.toString()}`);
      }
    }
    
    setSelectedSubcategory(subcategoryId);
  };

  const handleArchiveClick = (document_id) => {
    setShowSuccessMessage(true);
    setOpen(true);
    setArchiveId(document_id); 
  };
  
  const handleArchive = () => {
    setIsUpdating(true);

    setDocumentArchieve(archiveId)
      .then((response) => {
        setIsUpdating(false);
        if (response.data.success === true) {
          window.location.reload();
        }
      })
      .catch((err) => {
        console.error(err);
        setOpen(true);
        setIsUpdating(false);
      });
  };

  const { data: documentData, isLoading } = useSWR(
    w_id ? `documentData${w_id}-${selectedCategory}-${selectedSubcategory}-${currentPage}-${pagination?.limit}` : null,
    async () => {
      const { data } = await getDocumentByWorkspaceID(w_id, selectedCategory, selectedSubcategory, currentPage, pagination?.limit || 10);
      const filterData = data?.data.map((item) => {
        return { ...item, id: item?.document_id };
      });
      setPagination(data?.pagination);
      setTotalPages(data?.pagination?.totalPages);
      setTotalItems(data?.pagination?.total);
      setSelDocumentData(filterData || []);
      return filterData || [];
    },
    {
      revalidateOnFocus: false,
    }
  );

  const fetchData = useCallback((oFilter) => {
    const fetchId = ++fetchIdRef.current;
    if (fetchId === fetchIdRef.current && !isEqual(pagination, oFilter)) {
      // setPagination(oFilter);
    }
  }, [pagination]);

  useEffect(() => {
    if (pagination && pagination.total) {
      setPageCount(Math.ceil(pagination.total / pagination.limit));
    }
  }, [pagination]);

  const { data: petNames = [] } = useSWR(
    w_id ? `petNames${w_id}` : null,
    async () => {
      const { data } = await getPetNamesByWorkspaceId(w_id);
      return data?.data || [];
    },
    {
      revalidateOnFocus: false,
    }
  );

  
  let filteredDocumentData = useMemo(() => {

    let filteredDocumentData = selDocumentData;
    if (selectedPetName && filteredDocumentData) {
      filteredDocumentData = filteredDocumentData.filter((item) => {
        const petName = item.name?.toLowerCase() || "";
        const query = selectedPetName.toLowerCase();
        return petName.includes(query);
      });
    }

    if (selectedCategory && filteredDocumentData) {
      filteredDocumentData = filteredDocumentData.filter((item) => {
        return Number(item.category_id) === Number(selectedCategory);
      });
    }
    if (selectedSubcategory && filteredDocumentData) {
      filteredDocumentData = filteredDocumentData.filter((item) => {
        return Number(item.sub_category_id) === Number(selectedSubcategory);
      });
    }

    return filteredDocumentData;
  }, [selDocumentData, selectedPetName, selectedCategory, selectedSubcategory, categories, pagination]);


  const petNamesSet = new Set();
  const filteredPetNames = useMemo(() => {
    return petNames?.filter((pet) => {
      const lowercaseName = pet.name.toLowerCase();
      if (!petNamesSet.has(lowercaseName)) {
        petNamesSet.add(lowercaseName);
      return true;
    }
      return false;
    });
  }, [petNames, selectedPetName]);

  const ButtonItem = [
    {
      name: "yes",
      onClick: () => {
        handleArchive();
        setShowSuccessMessage(false);
      },
    },
    {
      name: "no",
      onClick: () => {
        setOpen(false);
        setShowSuccessMessage(false);
      },
    },
  ];

  const defaultOptions = {
    pageSize: 10,
  };

  const columns = useMemo(() => {
    return [
      {
        Header: "",
        accessor: "image",
        width: "8vw",
        disableSortBy: true,
        Cell: ({ row }) => {
          return row?.original?.image ? (
            <img
              alt="petImage"
              src={row?.original?.image}
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "10px",
                objectFit: "cover",
                margin: "0 auto",
                verticalAlign: "middle",
              }}
            />
          ) : (
            <AccountCircleIcon
              sx={{
                width: "50px",
                height: "50px",
              }}
            />
          );
        },
      },
      {
        Header: "Pet Name",
        accessor: "name",
        width: "12.5vw",
      },
      {
        Header: `Name Of Document`,
        accessor: "documment_name",
        width: "12.5vw",
      },
      {
        Header: "Date Of Document",
        accessor: "date",
        width: "12.5vw",
      },
      {
        Header: "Category",
        accessor: "category", // primary category name
        width: "15vw",
        Cell: ({ row }) => {
          const category = row?.original?.category || "";
          const subCategory = row?.original?.subCategory || ""; // sub category name
          return subCategory ? `${category} -> ${subCategory}` : category;
        },
      },
      {
        Header: "",
        accessor: "document_id",
        width: "8vw",
        isArchive: true,
        disableSortBy: true,
        disabled: isUpdating,
        textAlign: "center",
        Cell: ({ row }) => {
          return (
            <UnarchiveIcon
              style={{
                color: "#000000",
              }}
              onClick={() =>
                handleArchiveClick(row?.original?.document_id)
              }
            />
          );
        },
      },
      {
        Header: "Action",
        accessor: "action",
        width: "8vw",
        disableSortBy: true,
        textAlign: "center",
        Cell: ({ row }) => {
          return (
            <Button
              sx={{
                minWidth: "40px",
                background: `${hexToRgba(theme.palette.primary.main, 0.20)} !important`,
                borderRadius: "10px",
                marginRight: "5px",
                width: "24px",
              }}
              onClick={() => navigate(props.viewDataLink(row.original))}
            >
             <ViewIcon
                style={{
                  fill: `${theme.palette.primary.main}`, 
                  cursor: "pointer",
                }}
                alt="view"
              />
            </Button>
          );
        },
      },
    ];
  }, []);

  const categoryOptions = useMemo(() => {
    return categories.map(category => ({
      name: category.category,
      value: category.id
    }));
  }, [categories]);

  const selectedCategoryName = useMemo(() => {
    if (!selectedCategory) return "";
    const category = categoryOptions.find(cat => Number(cat.value) === Number(selectedCategory));
    return category ? category.name : "";
  }, [selectedCategory, categoryOptions]);

  // Get selected subcategory name for dropdown display
  const selectedSubcategoryName = useMemo(() => {
    if (!selectedSubcategory) return "";
    const formattedOptions = subcategoryOptions.map(sub => ({
      name: sub.name,
      value: sub.id
    }));
    const subcategory = formattedOptions.find(sub => Number(sub.value) === Number(selectedSubcategory));
    return subcategory ? subcategory.name : "";
  }, [selectedSubcategory, subcategoryOptions]);

  const props = {
    label: "Document",
    setSelectedUserName: setSelectedPetName,
    items: filteredPetNames,
    dataAddLink: "/document/form",
    columns,
    columnsData: filteredDocumentData,
    viewDataLink: (data) =>
    `/document/info/${data.document_id}`,
    placeholder: "pet name",
    details: "There are no documents for this user.",
    fetchData,
    pageCount,
    isLoading,
    pagination: true,
    paginationOptions: {
      totalPages,
      totalItems,
      currentPage,
      pageSize: 10,
      onPageChange: (page) => {
        setCurrentPage(page);
        setPagination({ ...pagination, page: page });
      },
      onPageSizeChange: (pageSize) => {
        setCurrentPage(1);
        setPagination({ ...pagination, limit: pageSize });
      }
    },
    defaultOptions,
    filterOptionsForCategory: {
      category: {
        label: "Category",
        options: categoryOptions,
        onChange: handleCategoryChange,
        value: selectedCategoryName
      },
      subcategory: {
        label: "Subcategory",
        options: subcategoryOptions.map(sub => ({
          name: sub.name,
          value: sub.id
        })),
        onChange: handleSubcategoryChange,
        value: selectedSubcategoryName,
        disabled: !selectedCategory
      }
    },
  };
  
  return (
    <>
      <PawItemList {...props} />
      {showSuccessMessage && (
        <>
          <DetailsModal
            open={open}
            severity="warning"
            buttonList={ButtonItem}
            title="Do you want to archive this document?"
            handleClose={()=> setOpen(false)}
          />
        </>
      )}
    </>
  );
}

const Loader = styled.div`
  justify-self: center;
  border: 5px solid #f3f3f3;
  border-radius: 50%;
  border-top: 5px solid #3498db;
  width: 12px;
  height: 12px;
  -webkit-animation: spin 2s linear infinite; /* Safari */
  animation: spin 2s linear infinite;

  /* Safari */
  @-webkit-keyframes spin {
    0% {
      -webkit-transform: rotate(0deg);
    }
    100% {
      -webkit-transform: rotate(360deg);
    }
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;